import cookie from 'cookie';
import {OrganizationJsonLd} from 'next-seo';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {wrapper} from '@jobseeker/common-core/stores';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {
  gladiatorService,
  jobfeedService,
} from '@jobseeker/common-core/services';

import Home from '@components/Home';
import NavLayout from '@layouts/NavLayout';
import {SeoLayout} from '@layouts/SeoLayout';

import {
  getConfigByApp,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {HOME, ROUTE_JOBFEED} from '@constants';
import {loadSEO} from '@helpers';

import type {GetServerSidePropsContext} from 'next/types';
import {getCookiesToDelete, setSSRCookies} from '@helpers/ssrMap';
import {
  InitiateUBA,
  TrackEventUBA,
} from '@jobseeker/common-core/utils/analytics';
import {useRouter} from 'next/router';
import {useEffect} from 'react';
import HomeV2 from '@components/HomeV2';

type HomePageProps = {
  seoConfig: object;
  enableCSR: boolean;
  metaData: object;
  BlogData: object;
  sortedJobs: Array<object>;
};

const HomePage = ({
  seoConfig,
  metaData,
  BlogData,
  sortedJobs,
}: HomePageProps) => {
  const router = useRouter();
  useEffect(() => {
    InitiateUBA('Home Page');
    TrackEventUBA('homeWithoutLoginPageView', 'view', {
      ref: router.query.ref || '',
      pageName: 'Home Page',
      pageView: 'signin',
      sessionUsername: `${sessionStorage.getItem('userId')}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <OrganizationJsonLd
        name={`${process.env.NEXT_PUBLIC_APP_NAME ?? 'iimjobs'}`}
        url={`https://www.${process.env.NEXT_PUBLIC_APP_NAME}${
          isAppIimjobs ? '.com' : '.tech'
        }`}
        logo={
          isAppIimjobs
            ? 'https://static.iimjobs.com/resources/images/iimjobs200x200.png'
            : 'https://staticlogo.hirist.com/webp/hirist-tech-logo.webp'
        }
        contactPoint={[{areaServed: 'IN', availableLanguage: 'en'}]}
        sameAs={[
          `https://www.facebook.com/${
            isAppIimjobs ? 'iimjobs' : 'hirist.jobs'
          }`,
          `https://www.linkedin.com/company/${
            isAppIimjobs ? 'iimjobs.com' : 'hirist-com'
          }`,
          `${
            isAppIimjobs
              ? 'https://twitter.com/iimjobs'
              : 'https://www.instagram.com/hirist.tech/'
          }`,
        ]}
      />
      {isAppIimjobs ? (
        <NavLayout page={HOME}>
          <Home />
        </NavLayout>
      ) : (
        <HomeV2 blogs={BlogData} sortedJobs={sortedJobs} />
      )}
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: HOME},
    });

    const isBotUser = isUserAgentForBot(context);

    console.log('🚀 ~ file: index.tsx:54 ~ store=> ~ isBotUser:', isBotUser);

    const seoConfig = await loadSEO(HOME);
    const metaData = getConfigByApp(metaInfo, HOME, true, 'homepage');
    const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
    let BlogData: object = {};
    if (!isAppIimjobs) {
      try {
        const fetchedData = await jobfeedService.getLatestBlogs('');
        BlogData = fetchedData;
      } catch (error) {
        console.log(`🚀 ~ store=> ~ ${HOME} ~ error:`, error);
      }
    }
    const {isMobile} = utilServerSideDeviceDetection(context);
    console.log('🚀 ~ store=> ~ Home Page --> isMobile:', isMobile);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });
          console.log(
            '🚀 ~ file: index.tsx:56 ~ store=> ~ validateInfo:',
            validateInfo,
            typeof validateInfo
          );

          if (
            validateInfo?.type &&
            ['old', 'refresh'].includes(validateInfo.type) &&
            validateInfo?.token
          ) {
            const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
              validateInfo.token
            );

            context.res?.setHeader('Set-Cookie', [
              ssrTokenCookie,
              ssrLastActCookie,
            ]);

            return {
              redirect: {
                permanent: false,
                destination: ROUTE_JOBFEED,
              },
            };
          } else if (validateInfo?.type === 'logout') {
            const cookiesTodelete = getCookiesToDelete();
            context.res?.setHeader('Set-Cookie', cookiesTodelete);
          } else if (validateInfo?.token) {
            return {
              redirect: {
                permanent: false,
                destination: ROUTE_JOBFEED,
              },
            };
          }
        }
      }
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      console.log('🚀 ~ file: index.tsx:71 ~ store=> ~ validate error:', error);

      const token = await gladiatorService.refresh('refresh', {
        headers: {
          cookie: context.req?.headers?.cookie,
        },
      });

      if (token) {
        const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(token);

        context.res?.setHeader('Set-Cookie', [
          ssrTokenCookie,
          ssrLastActCookie,
        ]);

        console.log('🚀 ~ file: index.tsx:68 ~ store=> ~ token:', token);

        return {
          redirect: {
            permanent: false,
            destination: ROUTE_JOBFEED,
          },
        };
      }
    }

    try {
      const {
        home: {
          section: {
            featuredEmployer,
            companyWorkWith,
            enhanceSkills,
            learningPartner,
            showcaseJobList,
          },
        },
      }: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
        metaData || {};

      if (featuredEmployer?.slotId) {
        const ads = await loadAds(featuredEmployer.slotId);

        if (ads) {
          store.dispatch({
            type: 'Ads/set_featured_employer',
            payload: removedUndefinedFromArrayObject(ads),
          });
        }
      }

      if (!isBotUser) {
        return {
          props: {
            enableCSR: true,
            seoConfig,
            metaData,
            BlogData,
          },
        };
      }

      if (companyWorkWith?.slotId) {
        const ads = await loadAds(companyWorkWith.slotId);

        if (ads) {
          store.dispatch({
            type: 'Ads/set_company_work_with',
            payload: removedUndefinedFromArrayObject(ads),
          });
        }
      }

      if (enhanceSkills?.slotId) {
        const ads = await loadAds(enhanceSkills.slotId);

        if (ads) {
          store.dispatch({
            type: 'Ads/set_enhance_skills',
            payload: removedUndefinedFromArrayObject(ads),
          });
        }
      }

      if (learningPartner?.slotId) {
        const ads = await loadAds(learningPartner.slotId);

        if (ads) {
          store.dispatch({
            type: 'Ads/set_learning_partner',
            payload: removedUndefinedFromArrayObject(ads),
          });
        }
      }

      if (
        showcaseJobList?.slotId &&
        process.env.NEXT_PUBLIC_APP_NAME === 'hirist'
      ) {
        const fetchedJobs = await loadAds(showcaseJobList.slotId);
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const sortedJobs = fetchedJobs?.sort(
          (a: any, b: any) =>
            parseInt(a.ordering, 10) - parseInt(b.ordering, 10)
        );
        return {
          props: {
            enableCSR: true,
            seoConfig,
            metaData,
            BlogData,
            sortedJobs: JSON.parse(JSON.stringify(sortedJobs)),
          },
        };
      }

      return {
        props: {
          seoConfig,
          metaData,
          BlogData,
        },
      };
    } catch (error) {
      console.log(`🚀 ~ SSR ~ /${HOME}=> ~ ERROR:`, error);

      return {
        props: {
          seoConfig,
          metaData,
          BlogData,
        },
      };
    }
  }
);

export default HomePage;
